import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Layout } from '../components/Layout.tsx';
import { apiClient } from '../api/client.ts';
import {
  UploadCloud, Download, CheckCircle2, AlertCircle, Loader2,
  RefreshCw, FileSpreadsheet, Info, Filter, History, Calendar,
  User as UserIcon, X, ArrowRight, Eye, ChevronRight
} from 'lucide-react';

interface RowLogItem {
  id?: string;
  rowNum: number;
  ticketId: string;
  ideaName: string;
  evaluationCategory?: string;
  assigneeEmail?: string;
  comments?: string;
  status: 'SUCCESS' | 'FAILED' | 'SKIPPED';
  reason: string;
}

interface ImportSummaryData {
  importLogId?: string;
  totalCount: number;
  successCount: number;
  failedCount: number;
  skippedCount: number;
  rowLogs: RowLogItem[];
}

interface ImportLogSession {
  id: string;
  totalCount: number;
  successCount: number;
  failedCount: number;
  skippedCount: number;
  importType?: string | null;
  createdAt: string;
  importedBy: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
}

export function BulkEvaluateImportPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'NEW_IMPORT' | 'HISTORY'>('NEW_IMPORT');

  // File Upload States
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [importResult, setImportResult] = useState<ImportSummaryData | null>(null);
  const [logFilter, setLogFilter] = useState<'ALL' | 'SUCCESS' | 'FAILED' | 'SKIPPED'>('ALL');

  // History Details Modal State
  const [selectedHistoryLogId, setSelectedHistoryLogId] = useState<string | null>(null);

  // Query past import logs list
  const { data: historyLogsData, isLoading: historyLoading, refetch: refetchHistory } = useQuery({
    queryKey: ['bulkImportLogsList'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/ideas/bulk-import-logs');
      return res.data.data as ImportLogSession[];
    },
  });

  // Query details for selected history log modal
  const { data: historyLogDetailsData, isLoading: detailsLoading } = useQuery({
    queryKey: ['bulkImportLogDetails', selectedHistoryLogId],
    queryFn: async () => {
      if (!selectedHistoryLogId) return null;
      const res = await apiClient.get(`/admin/ideas/bulk-import-logs/${selectedHistoryLogId}`);
      return res.data.data;
    },
    enabled: !!selectedHistoryLogId,
  });

  const evaluationHistoryLogs = (historyLogsData || []).filter(
    (log) =>
      !String(log.importType || '').startsWith('STATUS_UPDATE') && log.importType !== 'BULK_ASSIGNEE'
  );

  const handleDownloadSample = () => {
    const sampleData = [
      {
        'Ticket ID': 'CG1002345',
        'Idea Name': 'Optimize SAP Batch Process',
        'Saving Potential': 'Yes',
        'Ease of Implementation': 'Yes',
        'Time of Implementation': 'Yes',
        'Assignee Email': 'committee.member1@company.com',
        'Comments': 'High potential savings; priority evaluation',
      },
      {
        'Ticket ID': 'CG1002346',
        'Idea Name': 'Reduce Material Scrap Rate',
        'Saving Potential': 'Yes',
        'Ease of Implementation': 'Yes',
        'Time of Implementation': 'No',
        'Assignee Email': 'committee.member3@company.com',
        'Comments': 'Assigned for detailed feasibility review',
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sample Update Data');
    XLSX.writeFile(workbook, 'Idea_Evaluation_Import_Sample.xlsx');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setErrorMessage('');
    }
  };

  const handleUploadAndProcess = async () => {
    if (!file) {
      setErrorMessage('Please select an Excel file (.xlsx, .xls, or .csv) to import.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[firstSheetName];

      const rawRows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      if (rawRows.length === 0) {
        setErrorMessage('The selected Excel file contains no data rows.');
        setIsProcessing(false);
        return;
      }

      // Map Excel column names dynamically
      const parsedRows = rawRows.map((r: any, idx: number) => {
        const findVal = (possibleKeys: string[]) => {
          for (const key of Object.keys(r)) {
            const cleanKey = key.trim().toLowerCase();
            if (possibleKeys.some(p => cleanKey.includes(p.toLowerCase()))) {
              return r[key];
            }
          }
          return '';
        };

        const ticketId = findVal(['ticket id', 'ticketid', 'ticket_id', 'ticket']);
        const ideaName = findVal(['idea name', 'ideaname', 'idea_name', 'name of idea', 'title']);
        const savingPotential = findVal(['saving potential', 'saving_potential', 'saving']);
        const easeOfImplementation = findVal(['ease of implementation', 'ease_of_implementation', 'ease']);
        const timeOfImplementation = findVal(['time of implementation', 'time_of_implementation', 'time']);
        const evalCat = findVal(['idea evaluation category', 'evaluation category', 'eval category', 'category']);
        const assigneeEmail = findVal(['assignee email', 'assignee_email', 'assignee', 'user email', 'email']);
        const comments = findVal(['comments', 'comment', 'remarks', 'note']);

        return {
          rowNum: idx + 2,
          ticketId: String(ticketId).trim(),
          ideaName: String(ideaName).trim(),
          savingPotential,
          easeOfImplementation,
          timeOfImplementation,
          evaluationCategory: String(evalCat).trim(),
          assigneeEmail: String(assigneeEmail).trim(),
          comments: String(comments).trim(),
        };
      });

      const res = await apiClient.post('/admin/ideas/bulk-evaluate-import', {
        rows: parsedRows,
      });

      if (res.data.success) {
        setImportResult(res.data.data);
        queryClient.invalidateQueries({ queryKey: ['leaderIdeas'] });
        queryClient.invalidateQueries({ queryKey: ['adminIdeas'] });
        queryClient.invalidateQueries({ queryKey: ['adminStats'] });
        queryClient.invalidateQueries({ queryKey: ['bulkImportLogsList'] });
      } else {
        setErrorMessage(res.data.error?.message || 'Failed to process import.');
      }
    } catch (err: any) {
      console.error('Import Error:', err);
      setErrorMessage(err.response?.data?.error?.message || err.message || 'Error processing Excel file.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setImportResult(null);
    setErrorMessage('');
    setLogFilter('ALL');
  };

  const filteredLogs = importResult?.rowLogs.filter((log) => {
    if (logFilter === 'SUCCESS') return log.status === 'SUCCESS';
    if (logFilter === 'FAILED') return log.status === 'FAILED';
    if (logFilter === 'SKIPPED') return log.status === 'SKIPPED';
    return true;
  }) || [];

  return (
    <Layout title="Bulk Evaluation Import">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6 text-emerald-600" />
            Bulk Idea Evaluation & Assignees Import
          </h2>
          <p className="text-slate-500 text-xs mt-1">
            Update evaluation categories, assignees, and comments via Excel & maintain complete upload log history
          </p>
        </div>
        <button
          type="button"
          onClick={handleDownloadSample}
          className="px-3.5 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold text-xs rounded-xl shadow-sm transition-all flex items-center space-x-2 self-start sm:self-auto cursor-pointer"
        >
          <Download className="w-4 h-4 text-emerald-600" />
          <span>Download Sample Excel</span>
        </button>
      </div>

      {/* Main Tabs Navigation */}
      <div className="flex border-b border-slate-200 mb-6 bg-slate-50/50 p-1 rounded-xl">
        <button
          onClick={() => setActiveTab('NEW_IMPORT')}
          className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${activeTab === 'NEW_IMPORT'
            ? 'bg-white text-emerald-700 shadow-sm'
            : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
            }`}
        >
          <UploadCloud className="w-4 h-4" />
          <span>New Import Upload</span>
        </button>
        <button
          onClick={() => {
            setActiveTab('HISTORY');
            refetchHistory();
          }}
          className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${activeTab === 'HISTORY'
            ? 'bg-white text-emerald-700 shadow-sm'
            : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
            }`}
        >
          <History className="w-4 h-4" />
          <span>Import History Logs ({evaluationHistoryLogs.length})</span>
        </button>
      </div>

      {/* Tab 1: New Import View */}
      {activeTab === 'NEW_IMPORT' && (
        <div className="space-y-6">
          {!importResult ? (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
              {/* Requirements Banner */}
              <div className="bg-blue-50/70 border border-blue-200/80 rounded-xl p-4 flex items-start space-x-3">
                <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <div className="space-y-1 text-xs text-blue-900">
                  <p className="font-bold text-sm">Important Import Rules & Guidelines:</p>

                  <ul className="list-disc list-inside space-y-1.5 text-blue-800 font-medium">
                    <li><strong className="font-semibold text-blue-950">Matching Check:</strong> Both <strong className="font-semibold text-blue-950">Ticket ID</strong> and <strong className="font-semibold text-blue-950">Idea Name</strong> are required and must match the target idea.</li>
                    <li>
                      <strong className="font-semibold text-blue-950">Evaluation Criteria (Required):</strong> All three columns must be filled with <code className="bg-blue-100 px-1 py-0.5 rounded font-mono font-bold text-blue-900">Yes</code> or <code className="bg-blue-100 px-1 py-0.5 rounded font-mono text-blue-900">No</code>:
                      <span className="font-bold text-blue-950"> 1) Saving Potential</span>,
                      <span className="font-bold text-blue-950"> 2) Ease of Implementation</span>,
                      <span className="font-bold text-blue-950"> 3) Time of Implementation</span>. Empty or invalid values fail the row.
                    </li>
                    <li>
                      <strong className="font-semibold text-blue-950">At Least One Yes:</strong> At least one of the three criteria must be <code className="bg-blue-100 px-1 py-0.5 rounded font-mono font-bold text-blue-900">Yes</code> (all No fails the row).
                      Category: <span className="ml-1 text-emerald-800 font-bold">3 Yes = A</span> |
                      <span className="ml-1 text-blue-800 font-bold">2 Yes = B</span> |
                      <span className="ml-1 text-amber-800 font-bold">1 Yes = C</span>.
                    </li>
                    <li><strong className="font-semibold text-blue-950">Mandatory Comments:</strong> The <strong className="font-semibold text-blue-950">Comments</strong> field is required for every row in the Excel import.</li>
                    <li><strong className="font-semibold text-blue-950">Single Assignee Enforcement:</strong> Each idea receives only 1 primary Committee Member (non-executor). Among multiple emails, the first valid Committee Member in that idea’s committee scope is assigned; uploading replaces existing assignees.</li>
                    <li><strong className="font-semibold text-blue-950">Role Access:</strong> Admin, Committee Heads, and Committee Members can upload evaluations.</li>
                  </ul>
                </div>
              </div>

              {/* Error Message */}
              {errorMessage && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-xs flex items-start space-x-2">
                  <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Upload Dropzone */}
              <div className="border-2 border-dashed border-slate-300 hover:border-emerald-500 rounded-2xl p-10 text-center bg-slate-50/50 hover:bg-emerald-50/30 transition-all group cursor-pointer relative">
                <input
                  type="file"
                  accept=".xlsx, .xls, .csv"
                  onChange={handleFileChange}
                  className="absolute inset-0 opacity-0 cursor-pointer w-full h-full z-10"
                />
                <div className="flex flex-col items-center justify-center space-y-3 pointer-events-none">
                  <div className="p-4 bg-white shadow-sm border border-slate-200 rounded-2xl group-hover:scale-110 group-hover:border-emerald-300 transition-all">
                    <UploadCloud className="w-10 h-10 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-base font-bold text-slate-700">
                      {file ? file.name : 'Click to browse or drag & drop Excel file'}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">Supports .XLSX, .XLS, or .CSV file format</p>
                  </div>
                </div>
              </div>

              {file && (
                <div className="flex items-center justify-between p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800">
                  <span className="font-bold truncate">Selected File: {file.name}</span>
                  <span className="text-slate-500 font-mono">{(file.size / 1024).toFixed(1)} KB</span>
                </div>
              )}

              {/* Submit Action */}
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={handleUploadAndProcess}
                  disabled={!file || isProcessing}
                  className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold text-xs rounded-xl transition-all shadow-md shadow-emerald-600/20 flex items-center space-x-2 cursor-pointer"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Processing Excel Data...</span>
                    </>
                  ) : (
                    <>
                      <UploadCloud className="w-4 h-4" />
                      <span>Upload & Process Updates</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            /* Results & Logs Breakdown */
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 border-b border-slate-100 pb-4">
                <div>
                  <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    Import Processing Summary
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">Summary and row logs for current import session</p>
                </div>
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold text-xs rounded-xl transition-colors flex items-center space-x-1.5 self-start sm:self-auto cursor-pointer shadow-sm"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  <span>Upload Another File</span>
                </button>
              </div>

              {/* Summary KPI Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-slate-50 border border-slate-200 p-4 rounded-xl">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Total Rows</p>
                  <p className="text-2xl font-bold text-slate-800 mt-0.5">{importResult.totalCount}</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-xl">
                  <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">Successfully Updated</p>
                  <p className="text-2xl font-bold text-emerald-700 mt-0.5">{importResult.successCount}</p>
                </div>
                <div className="bg-red-50 border border-red-200 p-4 rounded-xl">
                  <p className="text-[10px] font-bold text-red-700 uppercase tracking-wider">Failed Records</p>
                  <p className="text-2xl font-bold text-red-700 mt-0.5">{importResult.failedCount}</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl">
                  <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider">Skipped Records</p>
                  <p className="text-2xl font-bold text-amber-700 mt-0.5">{importResult.skippedCount}</p>
                </div>
              </div>

              {/* Log Filters Bar */}
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 pt-2">
                <div className="flex items-center space-x-2">
                  <Filter className="w-4 h-4 text-slate-400" />
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Row Logs Breakdown:</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(['ALL', 'SUCCESS', 'FAILED', 'SKIPPED'] as const).map((filter) => (
                    <button
                      key={filter}
                      type="button"
                      onClick={() => setLogFilter(filter)}
                      className={`px-3 py-1 text-xs font-semibold rounded-full transition-all cursor-pointer ${logFilter === filter
                        ? filter === 'SUCCESS' ? 'bg-emerald-600 text-white shadow-sm'
                          : filter === 'FAILED' ? 'bg-red-600 text-white shadow-sm'
                            : filter === 'SKIPPED' ? 'bg-amber-500 text-white shadow-sm'
                              : 'bg-slate-800 text-white shadow-sm'
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                        }`}
                    >
                      {filter === 'ALL' ? `All (${importResult.rowLogs.length})`
                        : filter === 'SUCCESS' ? `Success (${importResult.successCount})`
                          : filter === 'FAILED' ? `Failed (${importResult.failedCount})`
                            : `Skipped (${importResult.skippedCount})`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Logs Table */}
              <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead className="bg-slate-50 sticky top-0 border-b border-slate-200 z-10">
                      <tr>
                        <th className="py-2.5 px-3 font-semibold text-slate-600 w-12 text-center">Row</th>
                        <th className="py-2.5 px-3 font-semibold text-slate-600">Ticket ID</th>
                        <th className="py-2.5 px-3 font-semibold text-slate-600">Idea Name</th>
                        <th className="py-2.5 px-3 font-semibold text-slate-600">Category</th>
                        <th className="py-2.5 px-3 font-semibold text-slate-600">Assignees</th>
                        <th className="py-2.5 px-3 font-semibold text-slate-600">Status</th>
                        <th className="py-2.5 px-3 font-semibold text-slate-600">Details / Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {filteredLogs.map((log, i) => (
                        <tr key={i} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-2 px-3 text-slate-400 font-mono text-center font-medium">{log.rowNum}</td>
                          <td className="py-2 px-3 font-semibold text-slate-700 whitespace-nowrap">{log.ticketId}</td>
                          <td className="py-2 px-3 text-slate-800 max-w-[180px] truncate" title={log.ideaName}>{log.ideaName}</td>
                          <td className="py-2 px-3 text-slate-600 font-mono">{log.evaluationCategory || '-'}</td>
                          <td className="py-2 px-3 text-slate-600 max-w-[160px] truncate" title={log.assigneeEmail}>{log.assigneeEmail || '-'}</td>
                          <td className="py-2 px-3 whitespace-nowrap">
                            {log.status === 'SUCCESS' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-full border border-emerald-200">
                                <CheckCircle2 className="w-3 h-3" /> SUCCESS
                              </span>
                            )}
                            {log.status === 'FAILED' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 text-[10px] font-bold rounded-full border border-red-200">
                                <AlertCircle className="w-3 h-3" /> FAILED
                              </span>
                            )}
                            {log.status === 'SKIPPED' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-bold rounded-full border border-amber-200">
                                <Info className="w-3 h-3" /> SKIPPED
                              </span>
                            )}
                          </td>
                          <td className={`py-2 px-3 ${log.status === 'FAILED' ? 'text-red-600 font-medium' : 'text-slate-600'}`}>
                            {log.reason}
                          </td>
                        </tr>
                      ))}
                      {filteredLogs.length === 0 && (
                        <tr>
                          <td colSpan={7} className="py-6 text-center text-slate-400 italic">
                            No logs found for this filter tab.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab 2: History & Maintenance Log List View */}
      {activeTab === 'HISTORY' && (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
          <div className="flex justify-between items-center border-b border-slate-100 pb-4">
            <div>
              <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <History className="w-5 h-5 text-emerald-600" />
                Past Import Logs Maintenance
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">View history and details of previous Excel import uploads</p>
            </div>
            <button
              onClick={() => refetchHistory()}
              className="px-3.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium text-xs rounded-xl transition-colors flex items-center space-x-1.5 cursor-pointer"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Refresh Logs</span>
            </button>
          </div>

          {historyLoading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            </div>
          ) : evaluationHistoryLogs.length > 0 ? (
            <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <table className="w-full text-left border-collapse text-xs">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="py-3 px-4 font-semibold text-slate-600">Import Date & Time</th>
                    <th className="py-3 px-4 font-semibold text-slate-600">Imported By</th>
                    <th className="py-3 px-4 font-semibold text-slate-600 text-center">Total</th>
                    <th className="py-3 px-4 font-semibold text-slate-600 text-center">Success</th>
                    <th className="py-3 px-4 font-semibold text-slate-600 text-center">Failed</th>
                    <th className="py-3 px-4 font-semibold text-slate-600 text-center">Skipped</th>
                    <th className="py-3 px-4 font-semibold text-slate-600 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {evaluationHistoryLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4 font-semibold text-slate-700 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-3.5 h-3.5 text-slate-400" />
                          <span>
                            {new Date(log.createdAt).toLocaleString('en-GB', {
                              day: '2-digit', month: 'short', year: 'numeric',
                              hour: '2-digit', minute: '2-digit', hour12: true
                            })}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-800">
                        <div className="flex items-center space-x-2">
                          <UserIcon className="w-3.5 h-3.5 text-slate-400" />
                          <div>
                            <p className="font-semibold text-slate-800">{log.importedBy?.name || 'System User'}</p>
                            <p className="text-[10px] text-slate-400">{log.importedBy?.email || ''} ({log.importedBy?.role || 'ADMIN'})</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center font-bold text-slate-700">{log.totalCount}</td>
                      <td className="py-3 px-4 text-center font-bold text-emerald-600 bg-emerald-50/50 rounded-lg">{log.successCount}</td>
                      <td className="py-3 px-4 text-center font-bold text-red-600 bg-red-50/50 rounded-lg">{log.failedCount}</td>
                      <td className="py-3 px-4 text-center font-bold text-amber-600 bg-amber-50/50 rounded-lg">{log.skippedCount}</td>
                      <td className="py-3 px-4 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => setSelectedHistoryLogId(log.id)}
                          className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg transition-colors flex items-center space-x-1 ml-auto cursor-pointer"
                        >
                          <Eye className="w-3.5 h-3.5 text-slate-500" />
                          <span>View Details</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 border border-dashed border-slate-200 rounded-xl bg-slate-50/50">
              <History className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm font-semibold text-slate-600">No import history logs found.</p>
              <p className="text-xs text-slate-400 mt-0.5">Upload a new Excel file to start maintaining import logs.</p>
            </div>
          )}
        </div>
      )}

      {/* History Details Modal */}
      {selectedHistoryLogId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-emerald-100 text-emerald-700 rounded-xl">
                  <History className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-800">Past Import Session Details</h3>
                  {historyLogDetailsData && (
                    <p className="text-xs text-slate-500">
                      Imported by <span className="font-semibold text-slate-700">{historyLogDetailsData.importedBy?.name}</span> on{' '}
                      {new Date(historyLogDetailsData.createdAt).toLocaleString('en-GB', {
                        day: '2-digit', month: 'short', year: 'numeric',
                        hour: '2-digit', minute: '2-digit', hour12: true
                      })}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setSelectedHistoryLogId(null)}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              {detailsLoading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
                </div>
              ) : historyLogDetailsData ? (
                <>
                  {/* KPI Cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-slate-50 border border-slate-200 p-3 rounded-xl">
                      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Total Rows</p>
                      <p className="text-2xl font-bold text-slate-800 mt-0.5">{historyLogDetailsData.totalCount}</p>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-xl">
                      <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">Updated</p>
                      <p className="text-2xl font-bold text-emerald-700 mt-0.5">{historyLogDetailsData.successCount}</p>
                    </div>
                    <div className="bg-red-50 border border-red-200 p-3 rounded-xl">
                      <p className="text-[10px] font-bold text-red-700 uppercase tracking-wider">Failed</p>
                      <p className="text-2xl font-bold text-red-700 mt-0.5">{historyLogDetailsData.failedCount}</p>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 p-3 rounded-xl">
                      <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider">Skipped</p>
                      <p className="text-2xl font-bold text-amber-700 mt-0.5">{historyLogDetailsData.skippedCount}</p>
                    </div>
                  </div>

                  {/* Rows Table */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                    <div className="max-h-80 overflow-y-auto">
                      <table className="w-full text-left border-collapse text-xs">
                        <thead className="bg-slate-50 sticky top-0 border-b border-slate-200 z-10">
                          <tr>
                            <th className="py-2.5 px-3 font-semibold text-slate-600 w-12 text-center">Row</th>
                            <th className="py-2.5 px-3 font-semibold text-slate-600">Ticket ID</th>
                            <th className="py-2.5 px-3 font-semibold text-slate-600">Idea Name</th>
                            <th className="py-2.5 px-3 font-semibold text-slate-600">Category</th>
                            <th className="py-2.5 px-3 font-semibold text-slate-600">Assignees</th>
                            <th className="py-2.5 px-3 font-semibold text-slate-600">Status</th>
                            <th className="py-2.5 px-3 font-semibold text-slate-600">Details / Reason</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {historyLogDetailsData.rows?.map((row: any) => (
                            <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                              <td className="py-2 px-3 text-slate-400 font-mono text-center font-medium">{row.rowNum}</td>
                              <td className="py-2 px-3 font-semibold text-slate-700 whitespace-nowrap">{row.ticketId}</td>
                              <td className="py-2 px-3 text-slate-800 max-w-[160px] truncate" title={row.ideaName}>{row.ideaName}</td>
                              <td className="py-2 px-3 text-slate-600 font-mono">{row.evaluationCategory || '-'}</td>
                              <td className="py-2 px-3 text-slate-600 max-w-[140px] truncate" title={row.assigneeEmail}>{row.assigneeEmail || '-'}</td>
                              <td className="py-2 px-3 whitespace-nowrap">
                                {row.status === 'SUCCESS' && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-full border border-emerald-200">
                                    <CheckCircle2 className="w-3 h-3" /> SUCCESS
                                  </span>
                                )}
                                {row.status === 'FAILED' && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 text-[10px] font-bold rounded-full border border-red-200">
                                    <AlertCircle className="w-3 h-3" /> FAILED
                                  </span>
                                )}
                                {row.status === 'SKIPPED' && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-bold rounded-full border border-amber-200">
                                    <Info className="w-3 h-3" /> SKIPPED
                                  </span>
                                )}
                              </td>
                              <td className={`py-2 px-3 ${row.status === 'FAILED' ? 'text-red-600 font-medium' : 'text-slate-600'}`}>
                                {row.reason}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              ) : null}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end px-6 py-4 border-t border-slate-100 bg-slate-50/50">
              <button
                type="button"
                onClick={() => setSelectedHistoryLogId(null)}
                className="px-6 py-2 bg-slate-800 hover:bg-slate-900 text-white font-semibold text-xs rounded-xl transition-all cursor-pointer shadow-sm"
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
